import {
    AddSelectionCriterionRequest,
    CreateTender,
    SelectionCriteriaCatalogItem,
    Tender,
    UpdateSelectionCriterionResultsRequest,
    UpdateTenderDto,
} from './dto/tender.dto';
import { PaginatedResponseDto } from 'src/infrastructure/admi-client/response.dto';
import { httpClient2 } from 'src/infrastructure/admi-client/http-client';
import {
    OfferSubmission,
    OfferSubmissionItemAssessmentType,
    OfferSubmissionListItem,
    UpdateOfferSubmissionDto,
} from './dto/offer-submission.dto';
import { AwardCriteria, UpdateAwardCriteriaWeights } from './dto/award-criteria.dto';
import { PriceComparison } from './dto/price-comparison.dto';
import { GenerateDocumentResult, DocumentType } from './dto/documents.dto';

export const admiTenderService = {
    async getTenders(page: number): Promise<PaginatedResponseDto<Tender>> {
        const response = await httpClient2.get<PaginatedResponseDto<Tender>>('/v1/tenders', { params: { page } });
        return response.data;
    },

    async createTender(dto: CreateTender, costEstimationFile: File): Promise<Tender> {
        const formData = new FormData();
        formData.append('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
        formData.append('costEstimation', costEstimationFile);

        const response = await httpClient2.post<Tender>('/v1/tenders', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    },

    async getTender(tenderId: string): Promise<Tender> {
        const response = await httpClient2.get<Tender>(`/v1/tenders/${tenderId}`);
        return response.data;
    },

    async updateTender(tenderId: string, dto: UpdateTenderDto): Promise<Tender> {
        const response = await httpClient2.put<Tender>(`/v1/tenders/${tenderId}`, dto);
        return response.data;
    },

    async deleteTender(tenderId: string) {
        await httpClient2.delete(`/v1/tenders/${tenderId}`);
    },

    async importOfferSubmission(
        tenderId: string,
        offerSubmissionFile: File | null,
        mimeType: string,
        offerProcessingFileId?: string,
    ): Promise<OfferSubmission> {
        const formData = new FormData();
        if (offerSubmissionFile) {
            formData.append(
                'offerSubmission',
                new Blob([offerSubmissionFile], { type: mimeType }),
                offerSubmissionFile.name,
            );
        }
        if (offerProcessingFileId) {
            formData.append('offerProcessingFileId', offerProcessingFileId);
        }

        const response = await httpClient2.post<OfferSubmission>(
            `/v1/tenders/${tenderId}/offer-submissions`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        );

        return response.data;
    },

    async getOfferSubmissions(tenderId: string) {
        const response = await httpClient2.get<OfferSubmissionListItem[]>(`/v1/tenders/${tenderId}/offer-submissions`);
        return response.data;
    },

    async getOfferSubmissionDetails(offerSubmissionId: string) {
        const response = await httpClient2.get<OfferSubmission>(`/v1/offer-submissions/${offerSubmissionId}`);
        return response.data;
    },

    async deleteOfferSubmission(offerSubmissionId: string) {
        const response = await httpClient2.delete(`/v1/offer-submissions/${offerSubmissionId}`);
        return response.data;
    },

    async updateOfferSubmission(offerSubmissionId: string, offerSubmission: UpdateOfferSubmissionDto) {
        const response = await httpClient2.patch<OfferSubmission>(
            `/v1/offer-submissions/${offerSubmissionId}`,
            offerSubmission,
        );
        return response.data;
    },

    async updateOfferSubmissionItemAssessmentFailReason(
        offerSubmissionId: string,
        ordinal: string,
        type: OfferSubmissionItemAssessmentType,
        reason: string,
    ) {
        const response = await httpClient2.put(
            `/v1/offer-submissions/${offerSubmissionId}/${ordinal}/assessment-fail-reasons/${type}`,
            { reason },
        );
        return response.data;
    },

    async deleteOfferSubmissionItemAssessmentFailReason(
        offerSubmissionId: string,
        ordinal: string,
        type: OfferSubmissionItemAssessmentType,
    ) {
        const response = await httpClient2.delete(
            `/v1/offer-submissions/${offerSubmissionId}/${ordinal}/assessment-fail-reasons/${type}`,
        );
        return response.data;
    },

    async getOfferSubmissionPdfPreview(offerSubmissionId: string) {
        const response = await httpClient2.get<Blob>(`/v1/offer-submissions/${offerSubmissionId}/pdf-preview`, {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Get Selection Criteria Catalog
     * Retrieves the predefined catalog of Selection Criteria that can be picked for a tender.
     * Items with selectedByDefault=true are automatically added to new tenders by the backend.
     */
    async getSelectionCriteriaCatalog(): Promise<SelectionCriteriaCatalogItem[]> {
        const response = await httpClient2.get<SelectionCriteriaCatalogItem[]>(
            '/v1/tenders/selection-criteria-catalog',
        );
        return response.data;
    },

    async getPriceComparison(tenderId: string) {
        const response = await httpClient2.get<PriceComparison>(`/v1/tenders/${tenderId}/price-comparison`);
        return response.data;
    },

    async getAwardCriteria(tenderId: string) {
        const response = await httpClient2.get<AwardCriteria>(`/v1/tenders/${tenderId}/award-criteria`);
        return response.data;
    },

    async changeAwardCriteriaWeights(tenderId: string, weights: UpdateAwardCriteriaWeights) {
        const response = await httpClient2.put<AwardCriteria>(
            `/v1/tenders/${tenderId}/award-criteria/weights`,
            weights,
        );
        return response.data;
    },

    /**
     * Add Selection Criterion to Tender
     * Adds a new selection criterion to the specified tender
     */
    async addSelectionCriterion(tenderId: string, request: AddSelectionCriterionRequest) {
        const response = await httpClient2.post(`/v1/tenders/${tenderId}/selection-criteria`, request);
        return response.data;
    },

    /**
     * Delete Selection Criterion from Tender
     * Removes a selection criterion from the specified tender
     */
    async deleteSelectionCriterion(tenderId: string, criterionId: string) {
        const response = await httpClient2.delete(`/v1/tenders/${tenderId}/selection-criteria/${criterionId}`);
        return response.data;
    },

    /**
     * Update Selection Criterion Results
     * Updates the results (offer submission IDs) for a specific selection criterion
     */
    async updateSelectionCriterionResults(
        tenderId: string,
        criterionId: string,
        results: UpdateSelectionCriterionResultsRequest,
    ) {
        const response = await httpClient2.patch(
            `/v1/tenders/${tenderId}/selection-criteria/${criterionId}/results`,
            results, // Send array directly, not wrapped in an object
        );
        return response.data;
    },

    async generateDocument(tenderId: string, documentType: DocumentType) {
        const response = await httpClient2.post<GenerateDocumentResult>(
            `/v1/tenders/${tenderId}/documents/${documentType}/generate`,
        );
        return response.data;
    },
};
